import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CountdownTimer } from './CountdownTimer';
import { BundleUpsellPopup } from './BundleUpsellPopup';
import { Check, Gift, Lock, CalendarCheck, ShoppingCart, ChefHat, ChevronDown, ClipboardList, Zap, Download, ShieldCheck, Star, UtensilsCrossed, Users } from 'lucide-react';
interface ValueStackProps {
  basePrice?: number;
  popupStrikethroughPrice?: string;
  popupSpecialPrice?: string;
  showFlashBonus?: boolean;
}
// Argentine number formatter: 19999 -> "19.999"
function formatARS(n: number) {
  return Math.round(n).toLocaleString('es-AR');
}
const BUMP_PRICE = 9499;
export function ValueStack({
  basePrice = 19999,
  popupStrikethroughPrice,
  popupSpecialPrice,
  showFlashBonus
}: ValueStackProps) {
  const [countdownVisible, setCountdownVisible] = useState(true);
  const [showBonusDetails, setShowBonusDetails] = useState(false);
  const [bump1Checked, setBump1Checked] = useState(false);
  const [bump2Checked, setBump2Checked] = useState(false);
  const [showBundlePopup, setShowBundlePopup] = useState(false);
  // Build dynamic cart URL based on bumps
  const buildCartUrl = () => {
    if (bump1Checked && bump2Checked) return 'https://nutripeques.myshopify.com/cart/51697731993816:1';
    if (bump1Checked) return 'https://nutripeques.myshopify.com/cart/51697730093272:1';
    if (bump2Checked) return 'https://nutripeques.myshopify.com/cart/51697730978008:1';
    return 'https://nutripeques.myshopify.com/cart/51697441472728:1';
  };
  const fireInitiateCheckout = () => {
    try {
      if (typeof (window as any).fbq === 'function') {
        ;
        (window as any).fbq('track', 'InitiateCheckout');
      }
    } catch (e) {
      // silently fail
    }
  };
  const handleCtaClick = () => {
    if (!bump1Checked && !bump2Checked) {
      setShowBundlePopup(true);
    } else {
      fireInitiateCheckout();
      window.location.href = buildCartUrl();
    }
  };
  // Calculate dynamic total price in ARS
  const calculateTotal = () => {
    let total = basePrice;
    if (bump1Checked) total += BUMP_PRICE;
    if (bump2Checked) total += BUMP_PRICE;
    return formatARS(total);
  };
  // Check on mount if the 24h timer already expired
  useEffect(() => {
    const startTime = localStorage.getItem('nutripeques-countdown-start');
    if (startTime) {
      const endTime = parseInt(startTime, 10) + 24 * 60 * 60 * 1000;
      if (Date.now() >= endTime) {
        setCountdownVisible(false);
      }
    }
  }, []);
  const stackItems = [{
    name: '📘 Guía de Fundamentos',
    value: '$23.100 ARS'
  }, {
    name: '🌞 Mañanas con Energía (16 desayunos)',
    value: '$12.320 ARS'
  }, {
    name: '🍲 Platos que Nutren (22 comidas)',
    value: '$18.480 ARS'
  }, {
    name: '🍎 Snacks Inteligentes (12 meriendas)',
    value: '$12.320 ARS'
  }, {
    name: '🎉 Gustitos Conscientes (10 recetas)',
    value: '$9.240 ARS'
  }, {
    name: '📋 Sistema NutriPeques planificación semanal',
    value: '$15.400 ARS'
  }, {
    name: '📊 Tabla de respuestas para conflictos en mesa',
    value: '$7.700 ARS'
  }, {
    name: '💡 Tips de congelación y reutilización',
    value: '$6.160 ARS'
  }];
  const bonusDetails = [{
    number: 1,
    icon: ChefHat,
    iconColor: 'text-[#6A1B9A]',
    iconBg: 'bg-[#F3E5F5]',
    accentColor: 'border-[#6A1B9A]',
    badgeBg: 'bg-[#6A1B9A]',
    title: '"Guía Mini-Chefs: Estrategia de Exposición"',
    value: '$27.720 ARS',
    image: 'https://cdn.magicpatterns.com/uploads/a6Dgd47E2nydzg7Q35XMHZ/minichefs.png',
    intro: 'Si tu hijo rechaza todo lo nuevo, el problema no empieza en el plato. La Guía Mini-Chefs te enseña a usar micro-tareas de 3 minutos para que tu hijo se exponga a nuevos alimentos sin presión.',
    features: ['El Mapa de Progreso (mirar → tocar → oler → probar)', 'Tareas por edad (1–2 / 3–4 / 5–7 años)', 'Scripts exactos para invitar sin presión y responder al "NO"', 'Plan de exposición de 7 días + tabla de seguimiento', '12 "Misiones Mini-Chefs" listas para usar', 'Checklist imprimible']
  }, {
    number: 2,
    icon: Users,
    iconColor: 'text-[#1565C0]',
    iconBg: 'bg-[#E3F2FD]',
    accentColor: 'border-[#1565C0]',
    badgeBg: 'bg-[#1565C0]',
    title: 'La Mesa en Familia — Comunidad Privada NutriPeques',
    value: '$120.000 ARS',
    image: 'https://cdn.magicpatterns.com/uploads/9rsoJU9kGtZj8cFEGBKwya/comunidadnutripeques.png',
    intro: 'El grupo privado de Facebook exclusivo para familias NutriPeques. Un espacio real, sin juicios, donde madres y padres que están exactamente donde estás vos comparten sus avances, dudas y victorias en la mesa. Eso no lo da un PDF — lo da una comunidad.',
    features: ['Acceso inmediato después de tu compra', 'Soporte real cuando una receta no sale como esperabas', 'Inspiración de familias reales (no de influencers)', 'Tips y recetas extra solo dentro del grupo', 'El lugar para celebrar cuando tu hijo prueba algo nuevo', 'Preguntas respondidas por la comunidad y el equipo NutriPeques']
  }];
  const bonusStackItems = [{
    icon: ChefHat,
    iconColor: 'text-[#6A1B9A]',
    iconBg: 'bg-[#F3E5F5]',
    name: 'Guía Mini-Chefs: Estrategia de Exposición',
    value: '$27.720 ARS',
    animate: false
  }, {
    icon: Users,
    iconColor: 'text-[#1565C0]',
    iconBg: 'bg-[#E3F2FD]',
    name: 'La Mesa en Familia — Comunidad Privada',
    value: '$120.000 ARS',
    animate: true
  }];
  return <section id="oferta" className="py-20 px-4 bg-gradient-to-b from-[#FFF8F0] to-[#FFE0B2]/50" data-id="element-577">
      <div className="max-w-lg md:max-w-3xl mx-auto" data-id="element-578">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-white rounded-[2rem] shadow-2xl overflow-hidden border-4 border-[#FF8A65] relative" data-id="element-579">
          {/* Header */}
          <div id="oferta-header" className="bg-[#FF8A65] text-white text-center py-6 px-4" data-id="element-580">
            <h2 className="text-2xl md:text-3xl font-black tracking-wide" data-id="element-581">
              TODO LO QUE NECESITÁS — UN SOLO PRECIO
            </h2>
          </div>

          <div className="p-6 md:p-10" data-id="element-582">
            {/* Stack List */}
            <div className="space-y-3 mb-4" data-id="element-583">
              {stackItems.map((item, idx) => <div key={idx} className="flex justify-between items-center border-b border-gray-100 pb-3" data-id="element-584">
                  <div className="flex items-center gap-2" data-id="element-585">
                    <Check className="w-5 h-5 text-[#4CAF50] shrink-0" data-id="element-586" />
                    <span className="text-gray-700 font-medium" data-id="element-587">
                      {item.name}
                    </span>
                  </div>
                  <span className="text-gray-400 line-through text-sm shrink-0 ml-4" data-id="element-588">
                    {item.value}
                  </span>
                </div>)}
              <div className="flex justify-between items-center bg-[#FFF3E0] p-3 rounded-xl border border-[#FFE0B2]" data-id="element-589">
                <div className="flex items-center gap-2" data-id="element-590">
                  <Gift className="w-5 h-5 text-[#E65100] shrink-0" data-id="element-591" />
                  <span className="text-[#E65100] font-bold" data-id="element-592">
                    Todas las actualizaciones futuras
                  </span>
                </div>
                <span className="text-[#E65100] font-black shrink-0 ml-4" data-id="element-593">
                  GRATIS
                </span>
              </div>
            </div>

            {/* Bonus items in stack */}
            <div className="mb-8" data-id="element-594">
              <div className="flex items-center gap-2 mb-3 mt-6" data-id="element-595">
                <Gift className="w-5 h-5 text-[#E65100]" data-id="element-596" />
                <span className="text-sm font-bold text-[#E65100] uppercase tracking-wider" data-id="element-597">
                  + 2 Regalos especiales gratis hoy
                </span>
              </div>

              <div className="space-y-2" data-id="element-598">
                {bonusStackItems.map((bonus, idx) => {
                const BonusIcon = bonus.icon;
                const rowContent = <>
                      <div className="flex items-center gap-2 min-w-0 flex-1" data-id="element-599">
                        <div className={`${bonus.iconBg} p-1.5 rounded-lg shrink-0`} data-id="element-600">
                          <BonusIcon className={`w-4 h-4 ${bonus.iconColor}`} data-id="element-601" />
                        </div>
                        <span className="text-gray-800 font-bold text-[13px] md:text-sm leading-tight" data-id="element-602">
                          {bonus.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0 ml-2" data-id="element-603">
                        <span className="text-gray-400 line-through text-[11px] md:text-xs" data-id="element-604">
                          {bonus.value}
                        </span>
                        <span className="bg-[#4CAF50] text-white text-[10px] font-bold px-2 py-0.5 rounded-full" data-id="element-605">
                          GRATIS
                        </span>
                      </div>
                    </>;
                if (bonus.animate) {
                  return <motion.div key={idx} animate={{
                    y: [0, -6, 0, -3, 0]
                  }} transition={{
                    duration: 1.6,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    repeatDelay: 0.4
                  }} className="flex justify-between items-center bg-gradient-to-r from-[#E3F2FD] to-[#FFFDE7] p-3 rounded-xl border-2 border-[#90CAF9] shadow-md" data-id="element-606">
                        {rowContent}
                      </motion.div>;
                }
                return <div key={idx} className="flex justify-between items-center bg-[#FFFDE7] p-3 rounded-xl border border-[#FFF9C4]" data-id="element-607">
                      {rowContent}
                    </div>;
              })}
              </div>

              {/* Leer más toggle */}
              <button onClick={() => setShowBonusDetails(!showBonusDetails)} className="mt-3 flex items-center gap-1.5 mx-auto text-[#E65100] font-bold text-sm hover:text-[#BF360C] transition-colors" data-id="element-608">
                {showBonusDetails ? 'Ver menos' : 'Ver detalles de los regalos'}
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${showBonusDetails ? 'rotate-180' : ''}`} data-id="element-609" />
              </button>

              {/* Expandable bonus details */}
              <AnimatePresence data-id="element-610">
                {showBonusDetails && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.4
              }} className="overflow-hidden" data-id="element-611">
                    <div className="mt-4 space-y-6" data-id="element-612">
                      {bonusDetails.map((bonus, idx) => {
                    const BonusDetailIcon = bonus.icon;
                    return <div key={idx} className={`bg-gray-50 rounded-2xl p-5 border-l-4 ${bonus.accentColor}`} data-id="element-613">
                            <div className="flex items-center gap-3 mb-3" data-id="element-614">
                              <div className={`${bonus.iconBg} p-2 rounded-xl`} data-id="element-615">
                                <BonusDetailIcon className={`w-5 h-5 ${bonus.iconColor}`} data-id="element-616" />
                              </div>
                              <div data-id="element-617">
                                <span className={`${bonus.badgeBg} text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider`} data-id="element-618">
                                  Regalo {bonus.number}
                                </span>
                                <h4 className="text-base font-black text-gray-900 mt-1" data-id="element-619">
                                  {bonus.title}
                                </h4>
                              </div>
                            </div>

                            <div className="flex justify-center mb-4" data-id="element-620">
                              <div className="relative" data-id="element-621">
                                <div className="absolute inset-0 bg-gradient-to-b from-orange-200/60 via-orange-100/40 to-transparent rounded-2xl blur-xl scale-110 -z-10" data-id="element-622" />
                                <img src={bonus.image} alt={bonus.title} className="w-full max-w-[220px] h-auto rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12),0_4px_12px_rgba(255,138,101,0.25)] transform hover:scale-[1.02] transition-transform duration-300" data-id="element-623" />
                              </div>
                            </div>

                            <p className="text-gray-600 text-sm mb-4 leading-relaxed" data-id="element-624">
                              {bonus.intro}
                            </p>

                            <div className="space-y-2" data-id="element-625">
                              <p className="text-xs font-bold text-gray-900 flex items-center gap-1.5" data-id="element-626">
                                <ClipboardList className="w-3.5 h-3.5 text-gray-500" data-id="element-627" />
                                Incluye:
                              </p>
                              <ul className="space-y-1.5" data-id="element-628">
                                {bonus.features.map((feature, fIdx) => <li key={fIdx} className="flex items-start gap-2 text-sm" data-id="element-629">
                                    <Check className="w-4 h-4 text-[#4CAF50] shrink-0 mt-0.5" data-id="element-630" />
                                    <span className="text-gray-700" data-id="element-631">
                                      {feature}
                                    </span>
                                  </li>)}
                              </ul>
                            </div>

                            <p className="text-center text-sm font-bold text-gray-900 mt-4" data-id="element-632">
                              Valor:{' '}
                              <span className="line-through text-gray-400" data-id="element-633">
                                {bonus.value}
                              </span>{' '}
                              <span className="text-[#4CAF50]" data-id="element-634">GRATIS hoy</span>
                            </p>
                          </div>;
                  })}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Full bundle mockup */}
            <div id="oferta-resumen" className="text-center mb-8 scroll-mt-4" data-id="element-635">
              <h3 className="text-lg md:text-xl font-black text-gray-900 leading-snug mb-1" data-id="element-636">
                Esto es todo lo que recibes hoy con tu compra de NutriPeques.
              </h3>
              <p className="text-base md:text-lg font-bold text-[#E65100] mb-5" data-id="element-637">
                Todo lo que necesitas para que coma bien
              </p>
              <div className="relative flex justify-center" data-id="element-638">
                <div className="absolute inset-0 bg-gradient-to-b from-orange-200/50 via-orange-100/30 to-transparent rounded-3xl blur-2xl scale-105 -z-10" data-id="element-639" />
                <motion.img src="https://cdn.magicpatterns.com/uploads/sEf5nMTQDwSGPzc8SmxKbh/nutripequesmasminichefs.png" alt="NutriPeques + Guía Mini-Chefs — Todo lo que recibes" className="w-full max-w-md md:max-w-lg h-auto rounded-3xl border-4 border-white shadow-[0_16px_50px_rgba(0,0,0,0.2),0_6px_20px_rgba(255,138,101,0.35),0_0_0_1px_rgba(0,0,0,0.05)]" animate={{
                y: [0, -8, 0]
              }} transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-640" />
              </div>
            </div>

            {/* Top 3 testimonials */}
            <div className="mb-8" data-id="element-641">
              <div className="flex items-center justify-center gap-2 mb-5" data-id="element-642">
                <Star className="w-5 h-5 fill-[#FFB300] text-[#FFB300]" data-id="element-643" />
                <h3 className="text-base md:text-lg font-black text-gray-900 tracking-wide text-center leading-snug" data-id="element-644">
                  3 historias de mamás con las mismas preguntas que vos, que
                  agradecen formar parte de la comunidad
                </h3>
                <Star className="w-5 h-5 fill-[#FFB300] text-[#FFB300]" data-id="element-645" />
              </div>
              <div className="space-y-4" data-id="element-646">
                {[{
                medal: '🥇',
                quote: 'Le mostré una receta a mi pediatra y me preguntó dónde la había conseguido. Le conté y me dijo que siguiera así porque los números de hierro de mi hija habían mejorado. Eso me lo dice todo.',
                name: 'Rocío',
                desc: 'Mamá de Emilia, 2 años · 🇦🇷 Argentina',
                img: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop&crop=face',
                accent: 'border-l-[#FFB300]'
              }, {
                medal: '🥈',
                quote: 'Hubo un momento en que me senté y lloré porque no sabía qué más hacer. Sentía que fallaba como mamá porque mi hijo no comía y yo me quedaba sin ideas. NutriPeques me devolvió la confianza en la mesa.',
                name: 'Natalia',
                desc: 'Mamá de Thiago, 3 años · 🇪🇸 España',
                img: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=200&h=200&fit=crop&crop=face',
                accent: 'border-l-[#9E9E9E]'
              }, {
                medal: '🥉',
                quote: 'Mi hija solo quería galletitas, jugos, todo de paquete. Yo ya había resignado. Pensé que era así y listo. Hoy come cosas que antes ni miraba. Sin pelea. Sin llanto. Sin que yo tenga que rogar.',
                name: 'Tatiana',
                desc: 'Mamá de Sofía, 3 años · 🇺🇸 USA',
                img: 'https://images.unsplash.com/photo-1542740348-39501cd6e2b4?w=200&h=200&fit=crop&crop=face',
                accent: 'border-l-[#CD7F32]'
              }].map((t, idx) => <motion.div key={idx} initial={{
                opacity: 0,
                y: 15
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: idx * 0.08
              }} className={`bg-white p-4 md:p-5 rounded-2xl shadow-sm border border-[#FFE0B2] border-l-4 ${t.accent} relative`} data-id="element-647">
                    <span className="absolute -top-2 -left-2 text-2xl drop-shadow-sm select-none" data-id="element-648">
                      {t.medal}
                    </span>
                    <div className="flex gap-0.5 mb-2" data-id="element-649">
                      {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-[#FFB300] text-[#FFB300]" data-id="element-650" />)}
                    </div>
                    <p className="text-gray-700 text-sm md:text-base italic mb-3 leading-relaxed" data-id="element-651">
                      "{t.quote}"
                    </p>
                    <p className="text-xs md:text-sm text-gray-600" data-id="element-652">
                      <span className="font-bold text-gray-900" data-id="element-653">{t.name}</span>
                      <span className="text-gray-400" data-id="element-654"> — </span>
                      {t.desc}
                    </p>
                  </motion.div>)}
              </div>
            </div>

            {/* Pricing Area */}
            <div className="bg-[#FDFBF7] rounded-2xl p-6 md:p-8 text-center border border-gray-200 mb-8 relative overflow-hidden" data-id="element-655">
              <div className="absolute top-0 right-0 bg-[#E65100] text-white text-xs font-bold px-3 py-1 rounded-bl-lg" data-id="element-656">
                67% DE DESCUENTO
              </div>

              <div className="mb-2" data-id="element-657">
                <span className="text-gray-500 font-medium" data-id="element-658">Valor total: </span>
                <span className="text-gray-400 line-through text-lg" data-id="element-659">
                  $143.220 ARS
                </span>
              </div>

              <div className="mb-4" data-id="element-660">
                <span className="text-gray-600 font-bold" data-id="element-661">
                  Precio regular:{' '}
                </span>
                <span className="text-gray-500 line-through text-xl" data-id="element-662">
                  $60.060 ARS
                </span>
              </div>

              <div className="text-center mb-2" data-id="element-663">
                <span className="block text-sm font-bold text-[#4CAF50] uppercase tracking-wider mb-1" data-id="element-664">
                  Oportunidad Única de Lanzamiento
                </span>
                <div className="flex justify-center items-start gap-1" data-id="element-665">
                  <span className="text-3xl font-bold text-[#2E7D32] mt-2" data-id="element-666">
                    $
                  </span>
                  <span className="text-6xl md:text-7xl font-black text-[#2E7D32] leading-none transition-all duration-300" data-id="element-667">
                    {calculateTotal()}
                  </span>
                  <span className="text-xl font-bold text-[#2E7D32] mt-2" data-id="element-668">
                    ARS
                  </span>
                </div>
              </div>
            </div>

            {/* Urgency — 24h countdown only when not expired */}
            {countdownVisible && <div className="text-center mb-8" data-id="element-669">
                <p className="text-[#D84315] font-bold mb-2" data-id="element-670">
                  ⏰ Esta oferta desaparece en:
                </p>
                <CountdownTimer hours={24} storageKey="nutripeques-countdown-start" onExpire={() => setCountdownVisible(false)} data-id="element-671" />
                <p className="text-gray-500 text-sm mt-2" data-id="element-672">
                  Después el precio sube a $60.060 ARS
                </p>
              </div>}

            {/* CTA and Order Bumps */}
            <div className="flex flex-col items-center" data-id="element-673">
              <button type="button" onClick={handleCtaClick} className="w-full min-h-[72px] bg-[#4CAF50] hover:bg-[#388E3C] text-white text-xl md:text-2xl font-black rounded-2xl shadow-[0_8px_0_#2E7D32] hover:shadow-[0_4px_0_#2E7D32] hover:translate-y-1 transition-all flex items-center justify-center text-center px-4 py-4 mb-4 cursor-pointer" data-id="element-674">
                → QUIERO EMPEZAR A NUTRIR CON CALMA
              </button>

              {/* Trust Badges — after first CTA */}
              <div className="w-full grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-id="element-675">
                <div className="flex items-center justify-center gap-2.5 bg-[#FFF8F0] border border-[#FFE0B2] rounded-xl py-3.5 px-3 shadow-[0_4px_12px_rgba(255,138,101,0.15)]" data-id="element-676">
                  <Zap className="w-5 h-5 text-[#FF8A65] shrink-0" data-id="element-677" />
                  <span className="text-gray-800 font-bold text-xs md:text-sm" data-id="element-678">
                    Acceso inmediato
                  </span>
                </div>
                <div className="flex items-center justify-center bg-white border border-[#BBDEFB] rounded-xl py-1 px-2 shadow-[0_4px_12px_rgba(0,150,255,0.15)] overflow-hidden" data-id="element-679">
                  <img src="https://cdn.magicpatterns.com/uploads/594Gg7CjuM898zM2Kyfci4/Mercado_Pago.svg.png" alt="Mercado Pago" className="h-14 md:h-16 max-w-full w-auto shrink-0 object-contain" data-id="element-680" />
                </div>
                <div className="flex items-center justify-center gap-2.5 bg-[#FFF8F0] border border-[#FFE0B2] rounded-xl py-3.5 px-3 shadow-[0_4px_12px_rgba(255,138,101,0.15)]" data-id="element-681">
                  <Download className="w-5 h-5 text-[#FF8A65] shrink-0" data-id="element-682" />
                  <span className="text-gray-800 font-bold text-xs md:text-sm" data-id="element-683">
                    Descarga directa
                  </span>
                </div>
                <div className="flex items-center justify-center gap-2.5 bg-[#E8F5E9] border border-[#C8E6C9] rounded-xl py-3.5 px-3 shadow-[0_4px_12px_rgba(76,175,80,0.15)]" data-id="element-684">
                  <ShieldCheck className="w-5 h-5 text-[#4CAF50] shrink-0" data-id="element-685" />
                  <span className="text-gray-800 font-bold text-xs md:text-sm" data-id="element-686">
                    Garantía de 15 días
                  </span>
                </div>
              </div>

              {/* ORDER BUMPS */}
              <div className="w-full mt-6 space-y-8" data-id="element-687">
                {/* Order Bump 1: Alfajores Saludables */}
                <div className="relative pt-3" data-id="element-688">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 z-20 bg-[#FF6F00] text-white text-[11px] md:text-xs font-black px-3 py-1 rounded-full shadow-md flex items-center gap-1 whitespace-nowrap" data-id="element-689">
                    <Star className="w-3.5 h-3.5 fill-current" data-id="element-690" /> ALTAMENTE
                    RECOMENDADO
                  </div>
                  <div onClick={() => setBump1Checked(!bump1Checked)} className={`relative cursor-pointer rounded-2xl border-[3px] border-solid transition-all duration-300 overflow-hidden ${bump1Checked ? 'border-[#4CAF50] bg-gradient-to-br from-[#E8F5E9] to-[#C8E6C9] shadow-lg shadow-green-100' : 'border-[#FFB74D] bg-gradient-to-br from-[#FFF8E1] to-[#FFF3E0] hover:border-[#FF9800] hover:shadow-md'}`} data-id="element-691">
                    {/* Shimmer Effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-[shimmer_3s_infinite] pointer-events-none" data-id="element-692" />

                    {/* Top ribbon */}
                    <div className="bg-[#FF8A65] text-white text-center py-2 px-4 font-black text-sm tracking-wide relative z-10" data-id="element-693">
                      🍪 OFERTA EXCLUSIVA — SUMALO A TU PEDIDO
                    </div>

                    <div className="p-4 md:p-5 relative z-10" data-id="element-694">
                      <div className="flex items-start gap-4" data-id="element-695">
                        {/* Checkbox */}
                        <div className={`mt-1 w-7 h-7 rounded-lg border-[3px] flex items-center justify-center shrink-0 transition-all duration-200 ${bump1Checked ? 'bg-[#4CAF50] border-[#4CAF50]' : 'border-gray-400 bg-white'}`} data-id="element-696">
                          {bump1Checked && <Check className="w-5 h-5 text-white" strokeWidth={3} data-id="element-697" />}
                        </div>

                        <div className="flex-1 min-w-0" data-id="element-698">
                          <p className="font-black text-gray-900 text-base md:text-lg leading-tight mb-2" data-id="element-699">
                            ¡SÍ! SUMAR ALFAJORES, COOKIES Y BOMBONES 🍫
                          </p>
                          <div className="flex flex-col sm:flex-row gap-3" data-id="element-700">
                            <div className="relative shrink-0" data-id="element-701">
                              <div className="absolute inset-0 bg-gradient-to-br from-orange-200/50 to-amber-100/40 rounded-xl blur-lg scale-110 -z-10" data-id="element-702" />
                              <img src="https://cdn.magicpatterns.com/uploads/6EwvWEpMVmnj3sdEJ44A7P/order_bump_1_recetario_alfajores_saludables.png" alt="Recetario de Alfajores, Cookies y Bombones Saludables" className="w-24 h-24 object-cover rounded-xl shadow-[0_6px_20px_rgba(0,0,0,0.1),0_3px_8px_rgba(255,138,101,0.3)] bg-white" data-id="element-703" />
                            </div>
                            <div data-id="element-704">
                              <p className="text-gray-700 text-sm leading-relaxed" data-id="element-705">
                                Evitá los ultraprocesados del súper. Llevate{' '}
                                <strong data-id="element-706">
                                  15 recetas de alfajores, cookies y bombones
                                  saludables
                                </strong>{' '}
                                (sin azúcar refinada) para que tu peque disfrute
                                de un "permitido" que realmente lo nutre.
                                Ideales para la vianda o meriendas rápidas.
                              </p>
                              <div className="mt-3 flex items-center gap-2" data-id="element-707">
                                <span className="text-gray-500 line-through text-sm font-medium" data-id="element-708">
                                  $19.999 ARS
                                </span>
                                <span className="bg-[#E65100] text-white font-black text-sm px-3 py-1 rounded-full shadow-sm" data-id="element-709">
                                  Solo $9.499 ARS
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order Bump 2: Cumpleaños Saludables */}
                <div className="relative pt-3" data-id="element-710">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 z-20 bg-[#FF6F00] text-white text-[11px] md:text-xs font-black px-3 py-1 rounded-full shadow-md flex items-center gap-1 whitespace-nowrap" data-id="element-711">
                    <Star className="w-3.5 h-3.5 fill-current" data-id="element-712" /> ALTAMENTE
                    RECOMENDADO
                  </div>
                  <div onClick={() => setBump2Checked(!bump2Checked)} className={`relative cursor-pointer rounded-2xl border-[3px] border-solid transition-all duration-300 overflow-hidden ${bump2Checked ? 'border-[#4CAF50] bg-gradient-to-br from-[#E8F5E9] to-[#C8E6C9] shadow-lg shadow-green-100' : 'border-[#CE93D8] bg-gradient-to-br from-[#F3E5F5] to-[#FCE4EC] hover:border-[#AB47BC] hover:shadow-md'}`} data-id="element-713">
                    {/* Shimmer Effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-[shimmer_3s_infinite] pointer-events-none" data-id="element-714" />

                    {/* Top ribbon */}
                    <div className="bg-[#7B1FA2] text-white text-center py-2 px-4 font-black text-sm tracking-wide relative z-10" data-id="element-715">
                      🎈 OFERTA ÚNICA — NO VOLVERÁ A APARECER
                    </div>

                    <div className="p-4 md:p-5 relative z-10" data-id="element-716">
                      <div className="flex items-start gap-4" data-id="element-717">
                        {/* Checkbox */}
                        <div className={`mt-1 w-7 h-7 rounded-lg border-[3px] flex items-center justify-center shrink-0 transition-all duration-200 ${bump2Checked ? 'bg-[#4CAF50] border-[#4CAF50]' : 'border-gray-400 bg-white'}`} data-id="element-718">
                          {bump2Checked && <Check className="w-5 h-5 text-white" strokeWidth={3} data-id="element-719" />}
                        </div>

                        <div className="flex-1 min-w-0" data-id="element-720">
                          <p className="font-black text-gray-900 text-base md:text-lg leading-tight mb-2" data-id="element-721">
                            🚀 CUMPLEAÑOS SALUDABLES: EL SISTEMA PARA FIESTAS
                            ÉPICAS
                          </p>
                          <div className="flex flex-col sm:flex-row gap-3" data-id="element-722">
                            <div className="relative shrink-0" data-id="element-723">
                              <div className="absolute inset-0 bg-gradient-to-br from-purple-200/50 to-pink-100/40 rounded-xl blur-lg scale-110 -z-10" data-id="element-724" />
                              <img src="https://cdn.magicpatterns.com/uploads/3mBhgGstWx4t5wPxfbNT7R/order_bump_2_libro_cumpleanos_saludable.png" alt="Cumpleaños Saludables: El Sistema para Fiestas Épicas" className="w-24 h-24 object-cover rounded-xl shadow-[0_6px_20px_rgba(0,0,0,0.1),0_3px_8px_rgba(123,31,162,0.25)] bg-white" data-id="element-725" />
                            </div>
                            <div data-id="element-726">
                              <p className="text-gray-700 text-sm leading-relaxed" data-id="element-727">
                                No dejes que los ultraprocesados arruinen su
                                salud en las fiestas. No son solo recetas — es
                                un <strong data-id="element-728">sistema completo</strong> con{' '}
                                <strong data-id="element-729">20 recetas</strong> de snacks, tortas y
                                bebidas saludables, más{' '}
                                <strong data-id="element-730">
                                  juegos diseñados para que los niños amen la
                                  comida real
                                </strong>{' '}
                                y se la pidan a sus papás después. ¡Paz mental
                                en cada festejo!
                              </p>
                              <div className="mt-3 flex items-center gap-2" data-id="element-731">
                                <span className="text-gray-500 line-through text-sm font-medium" data-id="element-732">
                                  $24.624 ARS
                                </span>
                                <span className="bg-[#7B1FA2] text-white font-black text-sm px-3 py-1 rounded-full shadow-sm" data-id="element-733">
                                  Solo $9.499 ARS
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="w-full grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8 mb-4" data-id="element-734">
                <div className="flex items-center justify-center gap-2.5 bg-[#FFF8F0] border border-[#FFE0B2] rounded-xl py-3.5 px-3 shadow-[0_4px_12px_rgba(255,138,101,0.15)]" data-id="element-735">
                  <Zap className="w-5 h-5 text-[#FF8A65] shrink-0" data-id="element-736" />
                  <span className="text-gray-800 font-bold text-xs md:text-sm" data-id="element-737">
                    Acceso inmediato
                  </span>
                </div>
                <div className="flex items-center justify-center bg-white border border-[#BBDEFB] rounded-xl py-1 px-2 shadow-[0_4px_12px_rgba(0,150,255,0.15)] overflow-hidden" data-id="element-738">
                  <img src="https://cdn.magicpatterns.com/uploads/594Gg7CjuM898zM2Kyfci4/Mercado_Pago.svg.png" alt="Mercado Pago" className="h-14 md:h-16 max-w-full w-auto shrink-0 object-contain" data-id="element-739" />
                </div>
                <div className="flex items-center justify-center gap-2.5 bg-[#FFF8F0] border border-[#FFE0B2] rounded-xl py-3.5 px-3 shadow-[0_4px_12px_rgba(255,138,101,0.15)]" data-id="element-740">
                  <Download className="w-5 h-5 text-[#FF8A65] shrink-0" data-id="element-741" />
                  <span className="text-gray-800 font-bold text-xs md:text-sm" data-id="element-742">
                    Descarga directa
                  </span>
                </div>
                <div className="flex items-center justify-center gap-2.5 bg-[#E8F5E9] border border-[#C8E6C9] rounded-xl py-3.5 px-3 shadow-[0_4px_12px_rgba(76,175,80,0.15)]" data-id="element-743">
                  <ShieldCheck className="w-5 h-5 text-[#4CAF50] shrink-0" data-id="element-744" />
                  <span className="text-gray-800 font-bold text-xs md:text-sm" data-id="element-745">
                    Garantía de 15 días
                  </span>
                </div>
              </div>

              {/* Summary Stack */}
              <div className="w-full bg-[#FAFAFA] rounded-2xl p-5 md:p-6 border border-gray-200 mt-4 mb-6 shadow-sm" data-id="element-746">
                <h4 className="text-center font-black text-gray-800 mb-4 text-sm tracking-widest uppercase" data-id="element-747">
                  RESUMEN DE TU PEDIDO
                </h4>
                <div className="w-12 h-1 bg-gray-200 mx-auto mb-5 rounded-full" data-id="element-748"></div>

                <div className="space-y-3 mb-5" data-id="element-749">
                  <div className="flex justify-between items-center text-sm md:text-base" data-id="element-750">
                    <span className="font-bold text-gray-800 flex items-center gap-2" data-id="element-751">
                      <span className="text-lg" data-id="element-752">📘</span> NutriPeques Completo
                    </span>
                    <div className="flex items-center gap-2" data-id="element-753">
                      <span className="text-gray-400 line-through text-xs md:text-sm" data-id="element-754">
                        $60.060 ARS
                      </span>
                      <span className="font-black text-[#2E7D32]" data-id="element-755">
                        ${formatARS(basePrice)} ARS
                      </span>
                    </div>
                  </div>

                  {bump1Checked && <div className="flex justify-between items-center text-sm md:text-base animate-in fade-in slide-in-from-top-2 duration-300" data-id="element-756">
                      <span className="font-bold text-gray-800 flex items-center gap-2" data-id="element-757">
                        <span className="text-lg" data-id="element-758">🍪</span> Alfajores, Cookies y
                        Bombones
                      </span>
                      <div className="flex items-center gap-2" data-id="element-759">
                        <span className="text-gray-400 line-through text-xs md:text-sm" data-id="element-760">
                          $19.999 ARS
                        </span>
                        <span className="font-black text-[#2E7D32]" data-id="element-761">
                          $9.499 ARS
                        </span>
                      </div>
                    </div>}

                  {bump2Checked && <div className="flex justify-between items-center text-sm md:text-base animate-in fade-in slide-in-from-top-2 duration-300" data-id="element-762">
                      <span className="font-bold text-gray-800 flex items-center gap-2" data-id="element-763">
                        <span className="text-lg" data-id="element-764">🎈</span> Cumpleaños
                        Saludables
                      </span>
                      <div className="flex items-center gap-2" data-id="element-765">
                        <span className="text-gray-400 line-through text-xs md:text-sm" data-id="element-766">
                          $24.624 ARS
                        </span>
                        <span className="font-black text-[#2E7D32]" data-id="element-767">
                          $9.499 ARS
                        </span>
                      </div>
                    </div>}
                </div>

                <div className="border-t border-gray-200 pt-4 flex justify-between items-center" data-id="element-768">
                  <span className="font-black text-gray-900 text-lg" data-id="element-769">
                    TOTAL HOY:
                  </span>
                  <span className="font-black text-[#2E7D32] text-2xl" data-id="element-770">
                    ${calculateTotal()} ARS
                  </span>
                </div>
              </div>

              {/* Second CTA Button */}
              <button type="button" onClick={handleCtaClick} className="w-full min-h-[72px] bg-[#4CAF50] hover:bg-[#388E3C] text-white text-xl md:text-2xl font-black rounded-2xl shadow-[0_8px_0_#2E7D32] hover:shadow-[0_4px_0_#2E7D32] hover:translate-y-1 transition-all flex items-center justify-center text-center px-4 py-4 cursor-pointer" data-id="element-771">
                → COMPLETAR MI PEDIDO AHORA
              </button>
            </div>
          </div>
        </motion.div>

        <p className="text-center text-gray-600 italic mt-8 text-lg" data-id="element-772">
          "No necesitas hacerlo perfecto. Necesitas empezar."
        </p>
      </div>

      <BundleUpsellPopup isOpen={showBundlePopup} onClose={() => setShowBundlePopup(false)} {...popupStrikethroughPrice ? {
      strikethroughPrice: popupStrikethroughPrice
    } : {}} {...popupSpecialPrice ? {
      specialPrice: popupSpecialPrice
    } : {}} data-id="element-773" />
    </section>;
}